import dayjs from 'dayjs';
import { toYyyyMmDdInput } from './inMarketDate';
import { exportToExcel } from './ProductSettingsExcelExporter';

// A row's override fields are "" once it has passed through the grid's addLocationHierarchy step,
// but undefined/null beforehand - treat both as "no override" so the default always shows through.
export const effectiveValue = (overrideValue, defaultValue) =>
    (overrideValue === undefined || overrideValue === null || overrideValue === '') ? defaultValue : overrideValue;

const toDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    const [year, month, day] = yyyyMmDd.split('-').map(Number);
    return new Date(year, month - 1, day);
};

export const buildProductSettingsExportColumns = ({ includeSeasonal, includeInMarketDate } = {}) => {
    const columns = [
        { key: 'division', header: 'Division', type: 'text' },
        { key: 'region', header: 'Region', type: 'text' },
        { key: 'district', header: 'District', type: 'text' },
        { key: 'sapId', header: 'Distributor SAP ID', type: 'text' },
        { key: 'name', header: 'Distributor Name', type: 'text' },
        { key: 'legal', header: 'Legal', type: 'boolean' },
        { key: 'palletQuantity', header: 'Pallet Quantity', type: 'number' },
        { key: 'palletDivisor', header: 'Pallet Divisor', type: 'number' },
        { key: 'unitWeight', header: 'Unit Weight', type: 'number' },
        { key: 'offeringStatus', header: 'Offering Status', type: 'text' },
        { key: 'fulfillmentMethod', header: 'Fulfillment Method', type: 'text' },
    ];

    if (includeSeasonal) {
        columns.push({ key: 'seasonal', header: 'Seasonal', type: 'boolean' });
    }
    if (includeInMarketDate) {
        columns.push({ key: 'inMarketDate', header: 'In-Market Date', type: 'date' });
    }
    if (includeInMarketDate && includeSeasonal) {
        columns.push({ key: 'endInMarketDate', header: 'End In-Market Date', type: 'date' });
    }

    columns.push({ key: 'customerPartNumber', header: 'Customer Part Number', type: 'text' });

    return columns;
};

// Expanded/effective values: an overridable field falls back to the SKU-level default when the
// distributor has no override, rather than being left blank. Matches the export on the
export const buildProductSettingsExportRows = (distributorRows, productDefaults) =>
    (distributorRows ?? []).map(row => ({
        division: row.division,
        region: row.region,
        district: row.district,
        sapId: row.sapId,
        name: row.name,
        legal: row.legal !== false,
        palletQuantity: effectiveValue(row.palletQuantity, productDefaults?.palletQuantity),
        palletDivisor: effectiveValue(row.palletDivisor, productDefaults?.palletDivisor),
        unitWeight: effectiveValue(row.unitWeight, productDefaults?.unitWeight),
        // Distributors with no fulfillment record have no skuOfferingStatus/skuFulfillmentType -
        // default the same way the grid's dropdowns do (SkuOfferingStatusSelect, SkuFulfillmentTypeSelect).
        offeringStatus: row.skuOfferingStatus?.description ?? 'Not Offered',
        fulfillmentMethod: row.skuFulfillmentType?.description ?? 'Removed',
        // null (not false) when the SKU is not seasonal: the cell is left blank rather than showing "No".
        seasonal: productDefaults?.seasonal ? true : null,
        inMarketDate: toDate(toYyyyMmDdInput(row.inMarketDateOverride) || toYyyyMmDdInput(row.inMarketDateDefault)),
        endInMarketDate: toDate(toYyyyMmDdInput(row.endInMarketDateOverride) || toYyyyMmDdInput(row.endInMarketDateDefault)),
        customerPartNumber: row.customerPartNumber,
    }));

export const handleProductSettingsExport = async ({ distributorRows, productDefaults, includeSeasonal, includeInMarketDate }) => {
    const columns = buildProductSettingsExportColumns({ includeSeasonal, includeInMarketDate });
    const rows = buildProductSettingsExportRows(distributorRows, productDefaults);

    const succeeded = await exportToExcel({
        filename: `ProductSettings_${productDefaults.sku}_${dayjs().format('MM-DD-YYYY')}`,
        sheetName: 'Product Settings',
        columns,
        rows,
    });

    if (!succeeded) {
        alert('There was an error exporting product settings. Please try again.');
    }
};
